import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Container
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Receipt,
  Print,
} from "@mui/icons-material";
import { returnAPI } from "../../services/API";
import { useSnackbar } from "notistack";

export const ReturnDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  console.log('ReturnDetails mounted with ID:', id);

  const [returnData, setReturnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, type: "" });

  useEffect(() => {
    fetchReturn();
  }, [id]);

  const fetchReturn = async () => {
    try {
      const response = await returnAPI.getById(id);
      console.log('API Response:', response);
      // The response data is nested under data.data according to your structure
      setReturnData(response?.data?.data || response?.data);
    } catch (error) {
      console.error('Error fetching return:', error);
      enqueueSnackbar("Error fetching return details", { variant: "error" });
      navigate("/hospital/pharmacy/returns");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await returnAPI.approve(returnData._id);
      enqueueSnackbar("Return approved successfully", { variant: "success" });
      fetchReturn();
      setActionDialog({ open: false, type: "" });
    } catch (error) {
      enqueueSnackbar("Error approving return", { variant: "error" });
    }
  };

  const handleComplete = async () => {
    try {
      await returnAPI.complete(returnData._id);
      enqueueSnackbar("Return completed successfully", { variant: "success" });
      fetchReturn();
      setActionDialog({ open: false, type: "" });
    } catch (error) {
      enqueueSnackbar("Error completing return", { variant: "error" });
    }
  };

  const handleReject = async () => {
    try {
      // You'll need to implement reject API call
      // await returnAPI.reject(returnData._id);
      enqueueSnackbar("Return rejected successfully", { variant: "success" });
      fetchReturn();
      setActionDialog({ open: false, type: "" });
    } catch (error) {
      enqueueSnackbar("Error rejecting return", { variant: "error" });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!returnData) {
    return (
      <Alert severity="error" sx={{ m: 2, fontSize: '1.1rem' }}>
        Return not found
      </Alert>
    );
  }

  // Extract data from the nested structure
  const {
    returnNumber,
    status,
    refundMethod,
    totalRefund,
    subtotal,
    taxAmount,
    items = [],
    saleInvoice,
    patient,
    createdBy,
    approvedBy,
    completedBy,
    approvedAt,
    completedAt,
    createdAt,
    notes,
    reason
  } = returnData;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Button 
            startIcon={<ArrowBack sx={{ fontSize: '1.3rem' }} />} 
            onClick={() => navigate("/hospital/pharmacy/returns")}
            sx={{ fontSize: '1.1rem' }}
          >
            Back to Returns
          </Button>
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Return #{returnNumber}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {status === "pending" && (
              <>
                <Button
                  startIcon={<CheckCircle sx={{ fontSize: '1.3rem' }} />}
                  variant="contained"
                  color="success"
                  onClick={() => setActionDialog({ open: true, type: "approve" })}
                  sx={{ fontSize: '1.1rem', py: 1.5 }}
                >
                  Approve
                </Button>
                <Button
                  startIcon={<Cancel sx={{ fontSize: '1.3rem' }} />}
                  variant="outlined"
                  color="error"
                  onClick={() => setActionDialog({ open: true, type: "reject" })}
                  sx={{ fontSize: '1.1rem', py: 1.5 }}
                >
                  Reject
                </Button>
              </>
            )}
            {status === "approved" && (
              <Button
                startIcon={<CheckCircle sx={{ fontSize: '1.3rem' }} />}
                variant="contained"
                onClick={() => setActionDialog({ open: true, type: "complete" })}
                sx={{ fontSize: '1.1rem', py: 1.5 }}
              >
                Complete
              </Button>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Patient and Invoice Information */}
          <Grid item xs={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5rem' }}>
                      Patient Information
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Name:</strong> {patient?.firstName} {patient?.lastName}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Patient ID:</strong> {patient?.patientId}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Phone:</strong> {patient?.phone}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      <strong>Email:</strong> {patient?.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5rem' }}>
                      Sale Information
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Invoice:</strong> {saleInvoice?.invoiceNumber}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 1 }}>
                      <strong>Sale Date:</strong> {new Date(saleInvoice?.saleDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      <strong>Original Total:</strong> ₹{saleInvoice?.grandTotal?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Return Items */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5rem' }}>
                  Return Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Medicine</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Batch</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Return Qty</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Reason</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Damaged</TableCell>
                        <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Refund Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={item._id || index}>
                          <TableCell>
                            <Typography variant="subtitle1" sx={{ fontSize: '1.1rem' }}>
                              {item.medicine?.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {item.medicine?.genericName} • {item.medicine?.brand}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {item.medicine?.strength} • {item.medicine?.form}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {item.batchStock?.batchNumber}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              Exp: {new Date(item.batchStock?.expiryDate).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {item.quantity} of {item.originalQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.reason}
                              size="medium"
                              color="primary"
                              variant="outlined"
                              sx={{ 
                                fontSize: '1rem',
                                '& .MuiChip-label': { fontSize: '1rem' }
                              }}
                            />
                            {item.description && item.description !== "other" && (
                              <Typography variant="body1" display="block" color="text.secondary" sx={{ fontSize: '1rem', mt: 0.5 }}>
                                {item.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.isDamaged ? "Yes" : "No"}
                              color={item.isDamaged ? "error" : "default"}
                              size="medium"
                              sx={{ 
                                fontSize: '1rem',
                                '& .MuiChip-label': { fontSize: '1rem' }
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                              ₹{item.refundAmount?.toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Summary and Actions */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: "sticky", top: 100 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5rem' }}>
                  Return Summary
                </Typography>

                {/* Status */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                    Status
                  </Typography>
                  <Chip
                    label={status.toUpperCase()}
                    color={
                      status === "completed"
                        ? "success"
                        : status === "approved"
                        ? "info"
                        : status === "rejected"
                        ? "error"
                        : "warning"
                    }
                    size="medium"
                    sx={{ 
                      fontSize: '1.1rem',
                      '& .MuiChip-label': { fontSize: '1.1rem', fontWeight: 'bold' }
                    }}
                  />
                </Box>

                {/* Refund Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mb: 1 }}>
                    Refund Method
                  </Typography>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem' }}>
                    {refundMethod?.toUpperCase()}
                  </Typography>
                  <Typography variant="h4" color="primary.main" sx={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    ₹{totalRefund?.toFixed(2)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Financial Breakdown */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Subtotal:</Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{subtotal?.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Tax:</Typography>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{taxAmount?.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="h5" sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>Total Refund:</Typography>
                    <Typography variant="h5" sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>₹{totalRefund?.toFixed(2)}</Typography>
                  </Box>
                </Box>

                {/* Timeline Information */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem' }}>
                    Timeline
                  </Typography>
                  <Typography variant="body1" display="block" sx={{ fontSize: '1rem', mb: 1 }}>
                    <strong>Created:</strong> {new Date(createdAt).toLocaleString()} by {createdBy?.name}
                  </Typography>
                  {approvedAt && (
                    <Typography variant="body1" display="block" sx={{ fontSize: '1rem', mb: 1 }}>
                      <strong>Approved:</strong> {new Date(approvedAt).toLocaleString()} by {approvedBy?.name}
                    </Typography>
                  )}
                  {completedAt && (
                    <Typography variant="body1" display="block" sx={{ fontSize: '1rem' }}>
                      <strong>Completed:</strong> {new Date(completedAt).toLocaleString()} by {completedBy?.name}
                    </Typography>
                  )}
                </Box>

                {/* Notes */}
                {notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem' }}>
                      Notes
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                      {notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Action Dialogs */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, type: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.5rem' }}>
          {actionDialog.type === "approve" && "Approve Return"}
          {actionDialog.type === "complete" && "Complete Return"}
          {actionDialog.type === "reject" && "Reject Return"}
        </DialogTitle>
        <DialogContent>
          <Alert 
            severity={
              actionDialog.type === "approve" ? "warning" : 
              actionDialog.type === "complete" ? "info" : "error"
            } 
            sx={{ mb: 2, fontSize: '1.1rem' }}
          >
            {actionDialog.type === "approve" &&
              "Are you sure you want to approve this return? This will process the refund and restock items."}
            {actionDialog.type === "complete" &&
              "Mark this return as completed? This will finalize the return process."}
            {actionDialog.type === "reject" &&
              "Are you sure you want to reject this return? This action cannot be undone."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setActionDialog({ open: false, type: "" })}
            sx={{ fontSize: '1.1rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={
              actionDialog.type === "approve" ? "success" :
              actionDialog.type === "complete" ? "primary" :
              "error"
            }
            onClick={
              actionDialog.type === "approve" ? handleApprove :
              actionDialog.type === "complete" ? handleComplete :
              handleReject
            }
            sx={{ fontSize: '1.1rem', py: 1 }}
          >
            {actionDialog.type === "approve" ? "Approve" :
             actionDialog.type === "complete" ? "Complete" :
             "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};